import Close from '@mui/icons-material/Close';
import HelpOutline from '@mui/icons-material/HelpOutline';
import LunchDining from '@mui/icons-material/LunchDining';
import Logout from '@mui/icons-material/Logout';
import PauseCircleFilled from '@mui/icons-material/PauseCircleFilled';
import Person from '@mui/icons-material/Person';
import PersonOff from '@mui/icons-material/PersonOff';
import PortableWifiOff from '@mui/icons-material/PortableWifiOff';
import PhoneCallback from '@mui/icons-material/PhoneCallback';
import RecordVoiceOver from '@mui/icons-material/RecordVoiceOver';
import Tty from '@mui/icons-material/Tty';
import {
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { useEffect, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import CallStatusChip from './CallStatusChip';
import { TENANT_ATTENDANCE_METRIC_ACCENT_COLOR } from './tenantAttendanceMetricConfig';
import UserTelephonyAvailabilityStatus from './users/UserTelephonyAvailabilityStatus';
import { useNumberFormat } from '../hooks/useNumberFormat';
import type {
  DashboardCockpitOperatorRow,
  DashboardCockpitWaitingCallRow,
  TenantAttendanceMetricsTelephony,
  UserTelephonyAvailability,
} from '../types';

const DISPLAY_FONT = '"Barlow Condensed", "Oswald", sans-serif';

function toAvailabilityPayload(operator: DashboardCockpitOperatorRow): UserTelephonyAvailability {
  return {
    status: operator.status,
    reason: operator.reason,
    unavailableSince: operator.unavailableSince,
    updatedAt: operator.unavailableSince,
    isAvailable: operator.status === 'available',
    isEligible: true,
  };
}

function getOperatorUnavailablePresentation(
  operator: DashboardCockpitOperatorRow,
  t: (key: string) => string,
  palette: Theme['palette'],
) {
  if (operator.runtimeReasonCode === 'logged_out') {
    return {
      label: t('pages:dashboard.lists.unavailableOperators.reasons.loggedOut'),
      icon: <Logout fontSize="small" />,
      chipSx: {
        bgcolor: alpha(palette.error.main, 0.14),
        color: palette.error.light,
        borderColor: alpha(palette.error.main, 0.28),
      },
    };
  }

  if (operator.runtimeReasonCode === 'disconnected') {
    return {
      label: t('pages:dashboard.lists.unavailableOperators.reasons.disconnected'),
      icon: <PortableWifiOff fontSize="small" />,
      chipSx: {
        bgcolor: alpha(palette.text.secondary, 0.12),
        color: palette.text.secondary,
        borderColor: alpha(palette.text.secondary, 0.3),
      },
    };
  }

  if (operator.runtimeReasonCode === 'presence_expired') {
    return {
      label: t('pages:dashboard.lists.unavailableOperators.reasons.presenceExpired'),
      icon: <PersonOff fontSize="small" />,
      chipSx: {
        bgcolor: alpha(palette.info.main, 0.16),
        color: palette.info.light,
        borderColor: alpha(palette.info.main, 0.3),
      },
    };
  }

  if (operator.reason === 'lunch') {
    return {
      label: t('common:operatorAvailability.states.lunch'),
      icon: <LunchDining fontSize="small" />,
      chipSx: {
        bgcolor: alpha(palette.warning.main, 0.18),
        color: palette.warning.light,
        borderColor: alpha(palette.warning.main, 0.36),
      },
    };
  }

  if (operator.reason) {
    return {
      label: t(`common:operatorAvailability.states.${operator.reason}`),
      icon: <PauseCircleFilled fontSize="small" />,
      chipSx: {
        bgcolor: alpha(palette.warning.main, 0.18),
        color: palette.warning.main,
        borderColor: alpha(palette.warning.main, 0.36),
      },
    };
  }

  return {
    label: t('pages:dashboard.lists.unavailableOperators.reasons.manualUnavailable'),
    icon: <PauseCircleFilled fontSize="small" />,
    chipSx: {
      bgcolor: alpha(palette.warning.main, 0.18),
      color: palette.warning.main,
      borderColor: alpha(palette.warning.main, 0.36),
    },
  };
}

function buildWaitingCallOfferSummaryLabel(
  call: DashboardCockpitWaitingCallRow,
  t: (key: string, options?: Record<string, unknown>) => string,
  formatNumber: (value: number) => string,
) {
  const offerCount = Number(call.currentRingingTargets?.length || 0);
  if (offerCount <= 0) {
    return null;
  }
  if (offerCount === 1) {
    return t('pages:dashboard.lists.waitingCalls.currentOffers.single');
  }
  return t('pages:dashboard.lists.waitingCalls.currentOffers.multiple', {
    count: formatNumber(offerCount),
  });
}

function buildAvailableOperatorOfferSummaryLabel(
  operator: DashboardCockpitOperatorRow,
  t: (key: string, options?: Record<string, unknown>) => string,
  formatNumber: (value: number) => string,
) {
  const offerCount = Number(operator.activeOfferCount || 0);
  if (offerCount <= 0) {
    return null;
  }
  if (offerCount === 1) {
    return t('pages:dashboard.lists.availableOperators.activeOffers.single');
  }
  return t('pages:dashboard.lists.availableOperators.activeOffers.multiple', {
    count: formatNumber(offerCount),
  });
}

function DetailHint({
  label,
  tooltipContent,
  tone = 'info',
}: {
  label: string;
  tooltipContent: ReactNode;
  tone?: 'info' | 'warning';
}) {
  const theme = useTheme();
  const accent = tone === 'warning' ? theme.palette.warning.main : theme.palette.info.main;

  return (
    <Tooltip title={tooltipContent} arrow>
      <Stack
        component="span"
        direction="row"
        alignItems="center"
        gap={0.45}
        sx={{
          width: 'fit-content',
          cursor: 'help',
          color: accent,
        }}
        tabIndex={0}
      >
        <Typography variant="caption" sx={{ color: 'inherit', fontWeight: 700 }}>
          {label}
        </Typography>
        <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
      </Stack>
    </Tooltip>
  );
}

function WaitingCallOfferTooltipContent({
  call,
  title,
}: {
  call: DashboardCockpitWaitingCallRow;
  title: string;
}) {
  const targets = call.currentRingingTargets || [];

  return (
    <Box sx={{ py: 0.35 }}>
      <Typography sx={{ fontWeight: 700, mb: 0.75 }}>{title}</Typography>
      <Stack gap={0.6}>
        {targets.map((target) => (
          <Typography key={target.offerKey} variant="caption" sx={{ display: 'block' }}>
            {target.displayLabel}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

function OperatorActiveOffersTooltipContent({
  operator,
  title,
  formatWaitLabel,
  noCampaignLabel,
}: {
  operator: DashboardCockpitOperatorRow;
  title: string;
  formatWaitLabel: (seconds: number) => string;
  noCampaignLabel: string;
}) {
  const activeOffers = operator.activeOffers || [];

  return (
    <Box sx={{ py: 0.35 }}>
      <Typography sx={{ fontWeight: 700, mb: 0.75 }}>{title}</Typography>
      <Stack gap={0.9}>
        {activeOffers.map((offer) => (
          <Box key={offer.callId}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
              {offer.contactName || offer.toNumber}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {offer.campaignName || noCampaignLabel}
            </Typography>
            {offer.waitSeconds != null ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {formatWaitLabel(offer.waitSeconds)}
              </Typography>
            ) : null}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function EmptyLaneState({ label }: { label: string }) {
  return (
    <Box
      sx={{
        minHeight: 140,
        px: 2.4,
        py: 2.2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '18px',
        border: (theme) => `1px dashed ${alpha(theme.palette.text.secondary, 0.28)}`,
        color: 'text.secondary',
      }}
    >
      <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: 320 }}>
        {label}
      </Typography>
    </Box>
  );
}

function DetailLane({
  accentColor,
  icon,
  title,
  children,
}: {
  accentColor: string;
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Stack direction="row" alignItems="center" gap={0.8} sx={{ mb: 1.4 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: accentColor,
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
      </Stack>
      <Stack gap={1.2} sx={{ minHeight: 0, overflowY: 'auto', pr: 0.4 }}>
        {children}
      </Stack>
    </Box>
  );
}

interface TenantAttendanceDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  telephony: TenantAttendanceMetricsTelephony | null;
}

/**
 * Render the header attendance drilldown using the same four-lane operational
 * layout already established in the dashboard.
 */
export default function TenantAttendanceDetailsDialog({
  open,
  onClose,
  telephony,
}: TenantAttendanceDetailsDialogProps) {
  const { t } = useTranslation(['common', 'pages']);
  const theme = useTheme();
  const { formatDuration, formatNumber } = useNumberFormat();
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    setNowMs(Date.now());
    const intervalId = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [open]);

  const availableOperators = telephony?.operatorsAvailable ?? [];
  const attendingCalls = telephony?.operatorsInAttendance ?? [];
  const unavailableOperators = telephony?.operatorsUnavailable ?? [];
  const waitingCalls = telephony?.waitingHumanAnswerCalls ?? [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: '90vw',
            maxWidth: '90vw',
            height: '90vh',
            maxHeight: '90vh',
            bgcolor: theme.palette.mode === 'dark' ? '#0c0c12' : '#ffffff',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.primary.main, 0.08),
        }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Tty color="primary" />
          <Typography variant="h6" sx={{ whiteSpace: 'nowrap' }}>
            {t('common:subscription.headerAttendance.detailsTitle')}
          </Typography>
        </Stack>
        <IconButton aria-label={t('common:actions.close')} onClick={onClose}>
          <Close />
        </IconButton>
      </Box>
      <DialogContent
        dividers
        sx={{
          p: { xs: 2, md: 2.5 },
          height: '100%',
          bgcolor: 'transparent',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', lg: 'repeat(4, minmax(0, 1fr))' },
            height: '100%',
            minHeight: 0,
          }}
        >
          <DetailLane
            accentColor={TENANT_ATTENDANCE_METRIC_ACCENT_COLOR.available}
            icon={<Person sx={{ fontSize: 20 }} />}
            title={t('pages:dashboard.lists.availableOperators.title')}
          >
            {availableOperators.length ? availableOperators.map((operator) => {
              const activeOfferSummaryLabel = buildAvailableOperatorOfferSummaryLabel(operator, t, formatNumber);
              return (
                <Box
                  key={operator.id}
                  component={RouterLink}
                  to={`/users/${operator.id}`}
                  sx={{
                    p: 1.6,
                    borderRadius: '18px',
                    textDecoration: 'none',
                    color: 'inherit',
                    border: `1px solid ${alpha(theme.palette.success.main, 0.18)}`,
                    backgroundColor: alpha(theme.palette.success.main, 0.06),
                    transition: 'transform 160ms ease, border-color 160ms ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      borderColor: alpha(theme.palette.success.main, 0.36),
                    },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" gap={1.5} alignItems="flex-start">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700 }} noWrap>
                        {operator.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {operator.extension
                          ? t('pages:dashboard.lists.availableOperators.extension', { value: operator.extension })
                          : operator.email}
                      </Typography>
                      {activeOfferSummaryLabel ? (
                        <Box sx={{ mt: 0.45 }}>
                          <DetailHint
                            label={activeOfferSummaryLabel}
                            tone="info"
                            tooltipContent={(
                              <OperatorActiveOffersTooltipContent
                                operator={operator}
                                title={t('pages:dashboard.lists.availableOperators.activeOffers.tooltipTitle')}
                                noCampaignLabel={t('pages:dashboard.noCampaign')}
                                formatWaitLabel={(seconds) => t(
                                  'pages:dashboard.lists.availableOperators.activeOffers.waitLabel',
                                  { duration: formatDuration(seconds) },
                                )}
                              />
                            )}
                          />
                        </Box>
                      ) : null}
                    </Box>
                    <UserTelephonyAvailabilityStatus
                      availability={toAvailabilityPayload(operator)}
                      showTimer={false}
                      direction="row"
                      gap={0.8}
                    />
                  </Stack>
                </Box>
              );
            }) : (
              <EmptyLaneState label={t('common:subscription.headerAttendance.empty.available')} />
            )}
          </DetailLane>

          <DetailLane
            accentColor={TENANT_ATTENDANCE_METRIC_ACCENT_COLOR.attending}
            icon={<RecordVoiceOver sx={{ fontSize: 20 }} />}
            title={t('pages:dashboard.lists.inAttendance.title')}
          >
            {attendingCalls.length ? attendingCalls.map((call) => (
              <Box
                key={call.id}
                sx={{
                  p: 1.6,
                  borderRadius: '18px',
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                  backgroundColor: alpha(theme.palette.info.main, 0.06),
                }}
              >
                <Stack gap={1.1}>
                  <Stack direction="row" justifyContent="space-between" gap={1.5} alignItems="center">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700 }} noWrap>
                        {call.operator?.name || t('pages:dashboard.notAvailable')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {call.contactName || call.toNumber}
                      </Typography>
                    </Box>
                    <CallStatusChip status={call.status} />
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {call.campaignName || t('pages:dashboard.noCampaign')}
                    </Typography>
                    <Button component={RouterLink} to={`/calls/${call.id}`} size="small">
                      {t('common:actions.view')}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            )) : (
              <EmptyLaneState label={t('common:subscription.headerAttendance.empty.attending')} />
            )}
          </DetailLane>

          <DetailLane
            accentColor={TENANT_ATTENDANCE_METRIC_ACCENT_COLOR.unavailable}
            icon={<PersonOff sx={{ fontSize: 20 }} />}
            title={t('pages:dashboard.lists.unavailableOperators.title')}
          >
            {unavailableOperators.length ? unavailableOperators.map((operator) => {
              const presentation = getOperatorUnavailablePresentation(operator, (key) => t(key), theme.palette);
              const shouldShowTimer = operator.runtimeReasonCode === 'manual_unavailable' && Boolean(operator.unavailableSince);
              return (
                <Box
                  key={operator.id}
                  component={RouterLink}
                  to={`/users/${operator.id}`}
                  sx={{
                    p: 1.6,
                    borderRadius: '18px',
                    textDecoration: 'none',
                    color: 'inherit',
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                    backgroundColor: alpha(theme.palette.warning.main, 0.06),
                    transition: 'transform 160ms ease, border-color 160ms ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      borderColor: alpha(theme.palette.warning.main, 0.38),
                    },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" gap={1.5} alignItems="center">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700 }} noWrap>
                        {operator.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {operator.extension
                          ? t('pages:dashboard.lists.unavailableOperators.extension', { value: operator.extension })
                          : operator.email}
                      </Typography>
                    </Box>
                    {shouldShowTimer ? (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', textAlign: 'right' }}>
                        <UserTelephonyAvailabilityStatus
                          availability={toAvailabilityPayload(operator)}
                          nowMs={nowMs}
                          showTimer
                          direction="column"
                          alignItems="flex-end"
                          gap={0.35}
                          timerFontSize="0.72rem"
                        />
                      </Box>
                    ) : (
                      <Chip
                        icon={presentation.icon}
                        label={presentation.label}
                        size="small"
                        variant="outlined"
                        sx={presentation.chipSx}
                      />
                    )}
                  </Stack>
                </Box>
              );
            }) : (
              <EmptyLaneState label={t('common:subscription.headerAttendance.empty.unavailable')} />
            )}
          </DetailLane>

          <DetailLane
            accentColor={TENANT_ATTENDANCE_METRIC_ACCENT_COLOR.waiting}
            icon={<PhoneCallback sx={{ fontSize: 20 }} />}
            title={t('pages:dashboard.lists.waitingCalls.title')}
          >
            {waitingCalls.length ? waitingCalls.map((call) => {
              const offerSummaryLabel = buildWaitingCallOfferSummaryLabel(call, t, formatNumber);
              return (
                <Box
                  key={call.id}
                  sx={{
                    p: 1.6,
                    borderRadius: '18px',
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                    backgroundColor: alpha(theme.palette.warning.main, 0.06),
                  }}
                >
                  <Stack gap={1.1}>
                    <Stack direction="row" justifyContent="space-between" gap={1.5} alignItems="center">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700 }} noWrap>
                          {call.contactName || call.toNumber}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {call.destinationLabel || t('pages:dashboard.humanQueue.noDestination')}
                        </Typography>
                        {offerSummaryLabel ? (
                          <Box sx={{ mt: 0.45 }}>
                            <DetailHint
                              label={offerSummaryLabel}
                              tone="warning"
                              tooltipContent={(
                                <WaitingCallOfferTooltipContent
                                  call={call}
                                  title={t('pages:dashboard.lists.waitingCalls.currentOffers.tooltipTitle')}
                                />
                              )}
                            />
                          </Box>
                        ) : null}
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: DISPLAY_FONT,
                          fontSize: '1.55rem',
                          lineHeight: 0.92,
                          color: theme.palette.warning.main,
                        }}
                      >
                        {call.waitSeconds != null ? formatDuration(call.waitSeconds) : t('pages:dashboard.notAvailable')}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {call.campaignName || t('pages:dashboard.noCampaign')}
                      </Typography>
                      <Button component={RouterLink} to={`/calls/${call.id}`} size="small">
                        {t('common:actions.view')}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              );
            }) : (
              <EmptyLaneState label={t('common:subscription.headerAttendance.empty.waiting')} />
            )}
          </DetailLane>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
